import { createReducer, on } from '@ngrx/store';
import { Attachment, Client, ClinicalNote, ClinicalNoteEmail, DocumentInstance, Patient, PatientRecordTask, Visit } from 'src/app/types/aggregate-graph.types';
import { clearStateRequested } from '../auth/auth.actions';
import * as patientTaskActions from '../patient-task/patient-task.actions';
import * as patientGraphActions from './patient-graph.actions';

export interface State {
	patientId: string;
	taskId: string;
	serviceId: string;
	patientLoaded: boolean;
	patient: Patient | null;
	clients: Client[];
	attachments: Attachment[];
	visits: Visit[];
	clinicalNotes: ClinicalNote[];
	tasks: PatientRecordTask[];
	documentInstances: DocumentInstance[];
}

export const initialState: State = {
	patientId: '',
	taskId: '',
	serviceId: '',
	patientLoaded: false,
	patient: null,
	clients: [],
	attachments: [],
	visits: [],
	clinicalNotes: [],
	tasks: [],
	documentInstances: []
}

export const patientGraphReducer = createReducer(
	initialState,
	on(patientGraphActions.subscribePatientRequested, (state, { patientId, taskId, serviceId }) => ({
		...state,
		patientId: patientId.toLocaleLowerCase(), // sometimes users paste in upper case id
		taskId: taskId.toLocaleLowerCase(), // sometimes users paste in upper case id
		serviceId: serviceId.toLocaleLowerCase() // sometimes users paste in upper case id
	})),
	on(patientGraphActions.patientGraphLoaded, (state, { patient, clients, attachments, visits, clinicalNotes, tasks, documentInstances }) => ({
		...state,
		patientLoaded: true,
		patient: patient,
		clients: clients,
		attachments: attachments,
		visits: visits,
		clinicalNotes: clinicalNotes,
		tasks: tasks,
		documentInstances: documentInstances
	})),
	on(patientGraphActions.patientUpdated, (state, { partialPatient }) => ({
		...state,
		patient: { ...state.patient!, ...partialPatient }
	})),
	on(patientGraphActions.communicationRecordAdded, (state, { record }) => ({
		...state,
		patient: { ...state.patient!, communicationRecords: [...state.patient!.communicationRecords, record] }
	})),
	on(patientGraphActions.communicationRecordUpdated, (state, { record }) => ({
		...state,
		patient: { ...state.patient!, communicationRecords: [...state.patient!.communicationRecords.filter(cr => cr.id !== record.id), record] }
	})),
	on(patientGraphActions.communicationRecordRemoved, (state, { recordId }) => ({
		...state,
		patient: { ...state.patient!, communicationRecords: state.patient!.communicationRecords.filter(cr => cr.id !== recordId) }
	})),
	on(patientGraphActions.clinicalNoteEmailAdded, (state, { email }) => ({
		...state,
		patient: { ...state.patient!, clinicalNoteEmails: [...state.patient!.clinicalNoteEmails, email] }
	})),
	on(patientGraphActions.attachmentAdded, (state, { attachment }) => ({
		...state,
		attachments: [...state.attachments, attachment]
	})),
	on(patientGraphActions.attachmentUpdated, (state, { partialAttachment }) => {
		const attachmentToUpdate = state.attachments.find(a => a.id === partialAttachment.id);
		if (attachmentToUpdate === undefined) return { ...state };
		const updatedAttachment = { ...attachmentToUpdate, ...partialAttachment };
		return {
			...state,
			attachments: [...state.attachments.filter(a => a.id !== updatedAttachment.id), updatedAttachment]
		};
	}),
	on(patientGraphActions.attachmentRemoved, (state, { id }) => ({
		...state,
		attachments: state.attachments.filter(a => a.id !== id)
	})),
	on(patientGraphActions.clientUpdated, (state, { partialClient }) => {
		const clientsToUpdate = state.clients.filter(c => c.id === partialClient.id);
		if (clientsToUpdate.length === 0) return { ...state };
		return {
			...state,
			clients: [...state.clients.filter(c => c.id !== partialClient.id), ...clientsToUpdate.map(client => ({ ...client, ...partialClient }))]
		}
	}),
	on(patientGraphActions.visitUpdated, (state, { partialVisit }) => {
		const visitToUpdate = state.visits.find(v => v.id === partialVisit.id);
		if (visitToUpdate === undefined) return { ...state };
		const updatedVisit = { ...visitToUpdate, ...partialVisit };
		return {
			...state,
			visits: [...state.visits.filter(v => v.id !== updatedVisit.id), updatedVisit]
		};
	}),
	on(patientGraphActions.clinicalNoteAdded, (state, { clinicalNote }) => ({
		...state,
		clinicalNotes: [...state.clinicalNotes, clinicalNote]
	})),
	on(patientGraphActions.clinicalNoteUpdated, (state, { partialClinicalNote }) => {
		const clinicalNoteToUpdate = state.clinicalNotes.find(cn => cn.id === partialClinicalNote.id);
		if (clinicalNoteToUpdate === undefined) return { ...state };
		const updatedClinicalNote = { ...clinicalNoteToUpdate, ...partialClinicalNote };
		return {
			...state,
			clinicalNotes: [...state.clinicalNotes.filter(cn => cn.id !== updatedClinicalNote.id), updatedClinicalNote]
		};
	}),
	on(patientGraphActions.taskAdded, (state, { task }) => ({
		...state,
		tasks: [...state.tasks, task]
	})),
	on(patientGraphActions.taskUpdated, (state, { partialTask }) => {
		const taskToUpdate = state.tasks.find(t => t.id === partialTask.id);
		if (taskToUpdate === undefined) return { ...state };
		const updatedTask = { ...taskToUpdate, ...partialTask };
		return {
			...state,
			tasks: [...state.tasks.filter(t => t.id !== updatedTask.id), updatedTask]
		};
	}),
	on(patientGraphActions.documentInstanceAdded, (state, { documentInstance }) => ({
		...state,
		documentInstances: [...state.documentInstances, documentInstance]
	})),
	on(patientGraphActions.documentInstanceUpdated, (state, { partialDocumentInstance }) => {
		const documentInstanceToUpdate = state.documentInstances.find(td => td.id === partialDocumentInstance.id);
		if (documentInstanceToUpdate === undefined) return { ...state };
		const updatedDocumentInstance = {
			...documentInstanceToUpdate,
			...partialDocumentInstance,
			widgetValues: { ...documentInstanceToUpdate.widgetValues, ...(partialDocumentInstance.widgetValues ?? {}) }
		};
		return {
			...state,
			documentInstances: [...state.documentInstances.filter(td => td.id !== updatedDocumentInstance.id), updatedDocumentInstance]
		};
	}),
	on(patientGraphActions.unsubscribePatientRequested, () => initialState),
	on(patientGraphActions.patientGraphSystemRestarted, (state) => ({
		...state,
		patient: null,
		clients: [],
		attachments: [],
		visits: [],
		clinicalNotes: [],
		tasks: [],
		documentInstances: []
	})),
	on(patientGraphActions.reloadPatientRequested, (state) => ({
		...state,
		patientLoaded: false,
		patient: null,
		taskId: initialState.taskId,
		clients: [],
		attachments: [],
		visits: [],
		clinicalNotes: [],
		tasks: [],
		documentInstances: []
	})),
	on(patientTaskActions.shareDraftTaskCompleted, (state, { taskId, firstSharedDate }) => {
		const taskToUpdate = state.tasks.find(t => t.id === taskId);
		if (taskToUpdate === undefined) return { ...state };
		return {
			...state,
			tasks: [...state.tasks.filter(t => t.id !== taskId), { ...taskToUpdate, firstSharedDate }]
		};
	}),
	on(clearStateRequested, () => initialState)
);